import React, { SyntheticEvent, useEffect, useState } from 'react';
import styles from './DateFilter.module.scss';
import filtersStyles from '../../Filters.module.scss';
import { format, subDays, startOfDay, endOfDay, addMonths, endOfYear } from 'date-fns';
import { Action, ClickDetector, CloseLight, DateRange } from 'esa-ui-library';
import { PropertyType, useStore } from '../../../../../common';
import { find } from 'lodash';
import useFilters from '../../../../../common/hooks/useFilters';

interface DateFilterProps {
	fullWidth?: boolean;
}

const DateFilter = ({ fullWidth }: DateFilterProps): JSX.Element => {
	const [{ filters }] = useStore();
	const [opened, setOpened] = useState(false);
	const [start, setStart] = useState(subDays(startOfDay(new Date()), 6));
	const [end, setEnd] = useState(endOfDay(new Date()));
	const [activeLabel, setActiveLabel] = useState('');
	const { onFilter } = useFilters();

	const setCurrent = (dates: PropertyType) => {
		const { startDate, endDate, customRange } = dates;
		setStart(startDate);
		setEnd(endDate);
		if (customRange) return setActiveLabel(customRange);
		setActiveLabel(`${format(startDate, 'd MMM yyyy')} - ${format(endDate, 'd MMM yyyy')}`);
	};

	const onDateChange = (dates: PropertyType) => {
		setCurrent(dates);
		const filterValue = [
			{
				name: 'start_date',
				value: dates.startDate,
			},
			{
				name: 'end_date',
				value: dates.endDate,
			},
		];
		onFilter(filterValue);
		setOpened(false);
	};

	const onDateRemove = (e: SyntheticEvent | null) => {
		e?.stopPropagation();

		setStart(subDays(startOfDay(new Date()), 6));
		setEnd(endOfDay(new Date()));
		setActiveLabel('');
		const filterValue = [
			{ name: 'start_date', value: '' },
			{ name: 'end_date', value: '' },
		];

		onFilter(filterValue);
	};

	const getLabelContent = () => {
		return (
			<>
				{activeLabel}
				<i className={filtersStyles.ClearFilter} onClick={onDateRemove}>
					<CloseLight size={16} />
				</i>
			</>
		);
	};

	useEffect(() => {
		const filterStart = find(filters, ['name', 'start_date'])?.value;
		const filterEnd = find(filters, ['name', 'end_date'])?.value;

		setStart(filterStart ? new Date(filterStart) : subDays(startOfDay(new Date()), 6));
		setEnd(filterEnd ? new Date(filterEnd) : endOfDay(new Date()));
		filterStart
			? setActiveLabel(`${format(new Date(filterStart), 'd MMM yyyy')} - ${format(new Date(filterEnd), 'd MMM yyyy')}`)
			: setActiveLabel('');
	}, [filters]);

	return (
		<section style={fullWidth ? { width: '100%' } : {}} className={styles.DateFilter}>
			<ClickDetector onOutsideClick={() => setOpened(false)} dataId='detector'>
				<Action
					select
					label='Date Range'
					labelContent={activeLabel ? getLabelContent() : ''}
					fluidHeight
					className={`${filtersStyles.Filter} ${styles.Filter} 
				${activeLabel.length ? filtersStyles.Active : ''} `}
					dropdownClass={styles.DateDropdown}
					onDropdownChange={setOpened}
					controlled
					opened={opened}
					dataId='date-filter'
				>
					<section onClick={(e) => e.stopPropagation()}>
						<DateRange
							start={start}
							end={end}
							onChange={onDateChange}
							onCancel={() => setOpened(false)}
							months={2}
							maxDate={endOfYear(addMonths(new Date(), 6))}
							customRanges
							time
						/>
					</section>
				</Action>
			</ClickDetector>
		</section>
	);
};

export { DateFilter };
